import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plug, Plus, Trash2, Loader2, Play, Pencil, Lock, Mic } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { ConnectionForm } from './ConnectionForm';
import { ConnectionTester } from './ConnectionTester';
import { ApiKeyGuide } from './ApiKeyGuide';
import { ConnectChoice } from './ConnectChoice';
import { LoginConnectionForm } from './LoginConnectionForm';
import { ProductCheck } from './ProductCheck';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const err = (e) => e.response?.data?.detail?.toString() || 'Something went wrong';
const syncMsg = { synced: 'Your Pro can now use it on calls', no_agent: 'Saved. It goes live once your Pro is set up', sync_failed: 'Saved, but we could not update your Pro yet. Try saving again' };

const ProLock = () => (
  <div className="rounded-xl border border-dashed border-border p-5 flex flex-wrap items-center justify-between gap-3" data-testid="api-connections-pro-lock">
    <p className="text-sm text-muted-foreground flex items-center gap-2"><Lock className="w-4 h-4" /> Connecting your own systems is part of the Pro plan.</p>
    <Link to="/pricing" className="text-sm font-medium text-primary hover:underline" data-testid="api-connections-upgrade-link">Upgrade to Pro</Link>
  </div>
);

const ConnectionRow = ({ c, onEdit, onDelete, onToggle, onCheck, testing, setTesting, params }) => (
  <div className="rounded-xl border border-border p-4" data-testid={`api-connection-${c.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-foreground">{c.name} <span className="ml-1 text-xs font-normal text-muted-foreground">{c.kind === 'login' ? `Login · ${c.username}` : 'API'}</span></p>
        <p className="text-sm text-muted-foreground">{c.description}</p>
        <p className="text-xs text-muted-foreground mt-1 break-all">{c.url}</p>
        {c.last_error && <p className="text-xs text-red-500 mt-1" data-testid={`api-connection-error-${c.id}`}>Needs attention: {c.last_error}</p>}
        {!c.last_error && (c.owner_checked_at
          ? <p className="text-xs text-green-600 mt-1" data-testid={`api-connection-checked-${c.id}`}>Checked by you · working</p>
          : <button type="button" onClick={onCheck} className="text-xs text-amber-600 hover:underline mt-1" data-testid={`api-connection-check-btn-${c.id}`}>Not checked yet: check it with 3 of your products</button>)}
        <p className="text-xs text-muted-foreground mt-1">{c.lookups ? `Used on calls ${c.lookups} times` : 'Not used on a call yet'}{c.last_test_ok === false ? ' · Last test failed' : c.last_test_ok ? ' · Last test worked' : ''}</p>
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={c.enabled} onCheckedChange={onToggle} aria-label="Turn connection on or off" data-testid={`api-connection-toggle-${c.id}`} />
        <Button size="sm" variant="ghost" onClick={() => setTesting(testing ? null : c.id)} aria-label="Test connection" title="Test" data-testid={`api-connection-test-btn-${c.id}`}><Play className="w-4 h-4" /></Button>
        <Button size="sm" variant="ghost" onClick={onEdit} aria-label="Edit connection" title="Edit" data-testid={`api-connection-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
        <Button size="sm" variant="ghost" onClick={onDelete} aria-label="Remove connection" title="Remove" data-testid={`api-connection-delete-${c.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
      </div>
    </div>
    {testing && <ConnectionTester connId={c.id} params={params} />}
  </div>
);

const TryIt = () => (
  <div className="rounded-xl bg-primary/5 border border-primary/30 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="api-connections-try-it">
    <p className="text-sm text-foreground">Hear it work: call your Pro from this browser and ask "Do you have ... in stock?"</p>
    <Link to="/voice" className="inline-flex items-center gap-2 rounded-lg bg-primary text-white px-4 py-2 text-sm font-medium hover:bg-primary/90 transition-colors" data-testid="api-connections-try-it-btn">
      <Mic className="w-4 h-4" /> Try it
    </Link>
  </div>
);

export const ApiConnectionsCard = ({ locationId = 'primary', businessId, embedded = false }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(null);
  const [checking, setChecking] = useState(null);
  const params = businessId ? { business_id: businessId } : {};

  const load = () => axios.get(`${API}/business/api-connections`, { params }).then((r) => setData(r.data)).catch(() => setData({ connections: [] }));
  useEffect(() => { load(); }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps
  const conns = (data?.connections || []).filter((c) => (c.location_id || 'primary') === locationId);

  const save = async (form) => {
    try {
      const body = { ...form, location_id: locationId };
      const r = form.id ? await axios.put(`${API}/business/api-connections/${form.id}`, body, { params }) : await axios.post(`${API}/business/api-connections`, body, { params });
      toast.success(syncMsg[r.data.sync] || 'Saved');
      if (!form.id) setChecking(r.data.connection.id);
      setEditing(null); load();
    } catch (e) { toast.error(err(e)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name}? Your Pro will stop using it.`)) return;
    try { await axios.delete(`${API}/business/api-connections/${c.id}`, { params }); toast.success('Removed'); load(); } catch (e) { toast.error(err(e)); }
  };
  const toggle = (c) => save({ ...c, enabled: !c.enabled, auth_value: '' });

  return (
    <div className={embedded ? '' : 'mb-8 scroll-mt-32'} id={embedded ? undefined : 'ai-connections'} data-testid={embedded ? `location-connections-${locationId}` : 'api-connections-section'}>
      {!embedded && (
        <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
          <Plug className="w-6 h-6 text-cyan-500" /> CONNECTED SYSTEMS
          <span className="text-xs bg-cyan-500/15 text-cyan-700 px-2 py-0.5 rounded-full font-medium">Pro</span>
        </h2>
      )}
      <Card className={embedded ? 'p-4 border-border' : 'card-modern p-6'}>
        <p className="text-sm text-muted-foreground mb-4">Connect the system where {embedded ? 'this location keeps' : 'you keep'} stock and prices. When a caller asks "Do you have this in stock?" or "How much is it?", your Pro checks it live and tells them.</p>
        {!data ? <Loader2 className="w-5 h-5 animate-spin" /> : !data.is_pro ? <ProLock /> : (
          <div className="space-y-3">
            {conns.map((c) => editing?.id === c.id
              ? (c.kind === 'login'
                ? <LoginConnectionForm key={c.id} initial={c} onSave={save} onCancel={() => setEditing(null)} />
                : <ConnectionForm key={c.id} initial={c} onSave={save} onCancel={() => setEditing(null)} />)
              : <React.Fragment key={c.id}>
                  <ConnectionRow c={c} onEdit={() => setEditing(c)} onDelete={() => remove(c)} onToggle={() => toggle(c)} onCheck={() => setChecking(c.id)} testing={testing === c.id} setTesting={setTesting} params={params} />
                  {checking === c.id && <ProductCheck conn={c} params={params} onDone={() => { setChecking(null); load(); }} onClose={() => setChecking(null)} />}
                </React.Fragment>)}
            {editing === 'api' ? <><ApiKeyGuide /><ConnectionForm onSave={save} onCancel={() => setEditing(null)} /></>
              : editing === 'login' ? <LoginConnectionForm onSave={save} onCancel={() => setEditing(null)} />
              : editing === 'pick' ? <ConnectChoice onPick={setEditing} />
              : !editing && <Button variant="outline" onClick={() => setEditing('pick')} data-testid="api-connection-add-btn"><Plus className="w-4 h-4 mr-2" /> Connect a system</Button>}
            {!embedded && conns.some((c) => c.enabled) && <TryIt />}
            <p className="text-xs text-muted-foreground" data-testid="api-connections-support">Don't sell products? You can skip this. Stuck? Email <a href="mailto:support@frontdeskpro.org" className="text-primary hover:underline">support@frontdeskpro.org</a> and we'll help.</p>
          </div>
        )}
      </Card>
    </div>
  );
};
